import React, { useState, useEffect } from 'react';
import { Slide, CreateSlideRequest } from '@/domain/entities/Slide';
import { slideService } from '@/application/services/SlideService';
import { uploadService } from '@/application/services';
import { HttpError } from '@/domain/entities';

export const SlidesPage: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSlide, setEditingSlide] = useState<Slide | null>(null);

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await slideService.getSlides(false); // Получаем все слайды
      setSlides(data);
    } catch (err) {
      console.error('Failed to load slides:', err);
      setError('Не удалось загрузить слайды');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Вы уверены, что хотите удалить этот слайд?')) return;

    try {
      await slideService.deleteSlide(id);
      loadSlides();
    } catch (err) {
      alert('Не удалось удалить слайд');
    }
  };

  const handleEdit = (slide: Slide) => {
    setEditingSlide(slide);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingSlide(null);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: CreateSlideRequest) => {
    try {
      if (editingSlide) {
        await slideService.updateSlide(editingSlide.id, data);
      } else {
        await slideService.createSlide(data);
      }
      setShowForm(false);
      setEditingSlide(null);
      loadSlides();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Произошла ошибка');
    }
  };

  const handleToggleActive = async (slide: Slide) => {
    try {
      await slideService.updateSlide(slide.id, { isActive: !slide.isActive });
      loadSlides();
    } catch (err) {
      alert('Не удалось обновить статус слайда');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600 dark:text-gray-400">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Управление слайдами</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Hero Slider на главной странице
              {!loading && slides.length > 0 && (
                <span className="ml-2 text-gray-700 dark:text-gray-300 font-medium">
                  • Найдено: {slides.length}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-semibold"
          >
            + Добавить слайд
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto min-h-0 mt-4">
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg mb-4">
            {error}
          </div>
        )}

        {showForm && (
          <SlideForm
            slide={editingSlide}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingSlide(null);
            }}
          />
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
        {slides.map((slide) => (
          <div
            key={slide.id}
            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex">
              {/* Preview Image */}
              <div className="w-64 h-40 flex-shrink-0 p-4">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>

              {/* Content */}
              <div className="flex-1 p-4 pl-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{slide.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{slide.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        slide.isActive
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {slide.isActive ? 'Активен' : 'Неактивен'}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-semibold">
                      Порядок: {slide.order}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{slide.description}</p>

                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Кнопка: {slide.buttonText}</span>
                  <span>→</span>
                  <span className="text-blue-600 dark:text-blue-400">{slide.buttonLink}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 p-4 border-l border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleEdit(slide)}
                  className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors whitespace-nowrap"
                >
                  Редактировать
                </button>
                <button
                  onClick={() => handleToggleActive(slide)}
                  className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors whitespace-nowrap ${
                    slide.isActive
                      ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/50'
                      : 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50'
                  }`}
                >
                  {slide.isActive ? 'Деактивировать' : 'Активировать'}
                </button>
                <button
                  onClick={() => handleDelete(slide.id)}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors whitespace-nowrap"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        ))}

        </div>

        {slides.length === 0 && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400 mt-4">
            Нет слайдов. Создайте первый слайд!
          </div>
        )}
      </div>
    </div>
  );
};

// Компонент формы для создания/редактирования слайда
interface SlideFormProps {
  slide: Slide | null;
  onSubmit: (data: CreateSlideRequest) => void;
  onCancel: () => void;
}

const SlideForm: React.FC<SlideFormProps> = ({ slide, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<CreateSlideRequest>({
    title: slide?.title || '',
    subtitle: slide?.subtitle || '',
    description: slide?.description || '',
    buttonText: slide?.buttonText || '',
    buttonLink: slide?.buttonLink || '',
    image: slide?.image || '',
    gradient: slide?.gradient || 'from-slate-900/80 via-slate-800/60 to-transparent',
    order: slide?.order || 1,
    isActive: slide?.isActive ?? true,
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.image) {
      setUploadError('Необходимо загрузить изображение');
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
          ? parseInt(value)
          : value,
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      setUploadError('Пожалуйста, выберите файл изображения');
      return;
    }

    // Проверка размера файла (максимум 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Размер файла не должен превышать 5MB');
      return;
    }

    setUploadingImage(true);
    setUploadError(null);
    
    try {
      const { url } = await uploadService.uploadImage(file, 'slides');
      setFormData((prev) => ({ ...prev, image: url }));
    } catch (err) {
      if (err instanceof HttpError) {
        setUploadError(err.data?.error || err.message || 'Не удалось загрузить изображение');
      } else if (err instanceof Error) {
        setUploadError(err.message);
      } else {
        setUploadError('Не удалось загрузить изображение');
      }
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const gradientOptions = [
    { value: 'from-slate-900/80 via-slate-800/60 to-transparent', label: 'Темно-серый' },
    { value: 'from-red-900/80 via-red-800/60 to-transparent', label: 'Красный' },
    { value: 'from-purple-900/80 via-purple-800/60 to-transparent', label: 'Фиолетовый' },
    { value: 'from-blue-900/80 via-blue-800/60 to-transparent', label: 'Синий' },
    { value: 'from-green-900/80 via-green-800/60 to-transparent', label: 'Зеленый' },
    { value: 'from-orange-900/80 via-orange-800/60 to-transparent', label: 'Оранжевый' },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            {slide ? 'Редактировать слайд' : 'Создать слайд'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Заголовок *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Подзаголовок *
              </label>
              <input
                type="text"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                required
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Описание *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Текст кнопки *
                </label>
                <input
                  type="text"
                  name="buttonText"
                  value={formData.buttonText}
                  onChange={handleChange}
                  required
                  maxLength={50}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ссылка кнопки *
                </label>
                <input
                  type="text"
                  name="buttonLink"
                  value={formData.buttonLink}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Изображение слайда *
              </label>
              
              {formData.image && (
                <div className="mb-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                  <img
                    src={formData.image}
                    alt="Превью"
                    className="w-full h-64 object-contain rounded-lg"
                  />
                </div>
              )}
              
              <div className="flex gap-2">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    {uploadingImage ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Загрузка...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{formData.image ? 'Изменить изображение' : 'Загрузить изображение'}</span>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="hidden"
                  />
                </label>
                
                {formData.image && (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, image: '' }))}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    disabled={uploadingImage}
                  >
                    Удалить
                  </button>
                )}
              </div>
              
              {uploadError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{uploadError}</p>
              )}
              
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Рекомендуемый размер: 1200x600px. Максимальный размер файла: 5MB
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Градиент *
                </label>
                <select
                  name="gradient"
                  value={formData.gradient}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {gradientOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Порядок отображения *
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleChange}
                  required
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
              />
              <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Активен (отображается на сайте)
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {slide ? 'Сохранить' : 'Создать'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

